import React from "react";
import { RowActionIcons } from "./RowActionIcons";
import "./TableShell.css";

/** @returns {string} Modifier classes for `.table-wrapper` frozen-column scrolling */
export function getTableFreezeColsClassName(freezeColumns = 0, freezeColumnsOffset = 0) {
  const parts = [];
  if (freezeColumns === 2) parts.push("table-wrapper--freeze-cols-2");
  if (freezeColumns === 2 && freezeColumnsOffset === 1) parts.push("table-wrapper--freeze-cols-offset-1");
  return parts.join(" ");
}

/**
 * Fills the space under the page chrome so a wide table's horizontal scrollbar
 * stays on the bottom edge of the window. Put summary cards, hints, and
 * pagination above `.table-scroll` as children. The table wrapper should use
 * `table-wrapper--sticky-header` when the header should stay visible; scrolling
 * stays on `.table-scroll` (see `tables.css`).
 *
 * @param {boolean} [fitSecondFreezeColumn] Size the second frozen data column to its text.
 *   Use for dynamic columns. Fixed schemas keep the shared freeze width.
 */
export function TableViewport({ children, className = "", fitSecondFreezeColumn = false }) {
  const classes = [
    "table-viewport",
    fitSecondFreezeColumn ? "table-viewport--fit-freeze-col" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return <div className={classes}>{children}</div>;
}

/**
 * Wraps data tables with shared empty/loading/footer patterns around `.table-wrapper`.
 * Use with existing `tables.css`. When `isEmpty && !loading`, children are not rendered (pass `null`).
 * While loading the first fetch, keep `isEmpty={false}` or pass `children={null}` so only the loading strip shows.
 *
 * @param {number} [freezeColumns=0] Pass `2` to freeze leading columns on horizontal scroll (see `tables.css`).
 * @param {number} [freezeColumnsOffset=0] Pass `1` to skip a leading column (e.g. checkbox) and freeze the next two.
 */
function TableShell({
  children,
  isEmpty = false,
  emptyMessage = "No rows to show.",
  emptyAction = null,
  loading = false,
  loadingLabel = "Loading…",
  footer = null,
  stickyHeader = false,
  freezeColumns = 0,
  freezeColumnsOffset = 0,
  className = "",
  wrapperClassName = "",
  "aria-label": ariaLabel,
}) {
  const freezeClass = getTableFreezeColsClassName(freezeColumns, freezeColumnsOffset);
  return (
    <div className={`table-shell ${className}`.trim()}>
      <div
        className={`table-wrapper ${stickyHeader ? "table-wrapper--sticky-header" : ""} ${freezeClass} ${wrapperClassName}`.trim()}
        aria-busy={loading ? true : undefined}
        aria-label={ariaLabel}
      >
        {loading && (
          <div className="table-shell__loading" role="status" aria-live="polite">
            <span className="table-shell__loading-text">{loadingLabel}</span>
          </div>
        )}
        {isEmpty && !loading ? (
          <div className="table-shell__empty" role="status">
            <p className="table-shell__empty-message">{emptyMessage}</p>
            {emptyAction ? <div className="table-shell__empty-action">{emptyAction}</div> : null}
          </div>
        ) : (
          children
        )}
      </div>
      {footer != null && footer !== false && <div className="table-shell__footer">{footer}</div>}
    </div>
  );
}

export default TableShell;

/**
 * Sortable column header: keyboard-activatable, `aria-sort`, works with parent sort state.
 *
 * @param {object} props
 * @param {string} props.sortKey
 * @param {string|null|undefined} props.currentSortKey
 * @param {'asc'|'desc'} props.direction
 * @param {(key: string) => void} props.onSort
 */
export function SortableTableTh({
  children,
  sortKey,
  currentSortKey,
  direction = "asc",
  onSort,
  className = "",
  ...thProps
}) {
  const active = currentSortKey === sortKey;
  const ariaSort = active ? (direction === "desc" ? "descending" : "ascending") : "none";

  return (
    <th scope="col" aria-sort={ariaSort} className={className} {...thProps}>
      <button
        type="button"
        className="table-shell__sort-btn"
        onClick={() => onSort && onSort(sortKey)}
      >
        <span className="table-shell__sort-btn-label">{children}</span>
        {active && <span className="table-shell__sort-indicator">{direction === "desc" ? "▼" : "▲"}</span>}
      </button>
    </th>
  );
}

/**
 * Narrow Action column for row actions. Uses platform hover-⋮ menu via RowActionIcons.
 */
export function TableRowActionsCell({ children, className = "", ...tdProps }) {
  return (
    <td {...tdProps} className={`table-shell__actions ${className}`.trim()}>
      <RowActionIcons>{children}</RowActionIcons>
    </td>
  );
}

/**
 * Compact action-column header: gear icon instead of "Action" label.
 * When `onCustomizeColumns` is set, the gear opens the column customizer (replaces legacy Action + ☰).
 *
 * @param {object} props
 * @param {'left'|'center'|'right'} [props.align='center']
 * @param {() => void} [props.onCustomizeColumns]
 * @param {boolean} [props.customizeDisabled]
 */
export function TableActionColumnHeader({
  className = "",
  align = "center",
  onCustomizeColumns,
  customizeDisabled = false,
  ...thProps
}) {
  const alignClass =
    align === "center" ? "text-center" : align === "right" ? "text-right" : align === "left" ? "text-left" : "";
  const thClass = ["table-col-action", "table-action-col-header", alignClass, className]
    .filter(Boolean)
    .join(" ");

  const gear = onCustomizeColumns ? (
    <button
      type="button"
      onClick={onCustomizeColumns}
      title="Customize columns"
      aria-label="Customize columns"
      className="table-action-col-header__btn btn-link"
      disabled={customizeDisabled}
    >
      ⚙
    </button>
  ) : (
    <span className="table-action-col-header__icon" aria-hidden="true">
      ⚙
    </span>
  );

  return (
    <th scope="col" aria-label="Actions" className={thClass} {...thProps}>
      {gear}
    </th>
  );
}
